<template>
  <div id="tac-admin-sales-report-low-price-alert__list" class="tac-admin-sales-report-low-price-alert__list">
    <ol class="breadcrumb">
      <li><a href="/admin">Admin</a></li>
      <li><a href="/admin/sales">Sales</a></li>
      <li><a href="/admin/sales/report">Report</a></li>
      <li class="active">Low Price Alert</li>
    </ol>
    <div class="page-header">
      <h1 class="page-header-title">
        <span class="fa fa-fw fa-exchange"></span>
        Low Price Alert Report
      </h1>
      <div class="page-header-actions page-header-actions--right-position">
        <div class="form-inline form-inline--better-spaced form-inline--compact form-inline--one-liner">
          <div class="form-group form-group-sm" title="Products Carried By Vendor">
            <label class="sr-only">Category</label>
            <div class="input-group input-group-sm">
              <select class="form-control input-sm" v-model="filter.categoryId">
                <option :value="null">Root</option>
                <option v-for="(opt, key) in categoryOptions" :value="opt.value" :key="key" :class="`opt-level-${opt.level}`">{{ optSpacer('&nbsp;', opt.level * 2) + opt.label }}</option>
              </select>
              <span class="input-group-btn">
                <button class="btn btn-default" type="button" title="Filter Category"><i class="fa fa-fw fa-filter"></i></button>
              </span>
            </div>
          </div>
          <div class="form-group form-group-sm" title="Product ID">
            <label class="sr-only">Product ID</label>
            <input type="text" class="form-control input-sm" v-model="filter.productId" @keypress.enter="update" />
          </div>
          <div class="form-group form-group-sm" title="Products Carried By Vendor">
            <label class="sr-only">Products Carried By Vendor</label>
            <select class="form-control input-sm" v-model="filter.vendor">
              <option value="all">All</option>
              <option value="withapi">Vendors w/ API</option>
              <option value="ss">SS</option>
              <option value="rsr">RSR</option>
              <option value="tac">TAC</option>
            </select>
          </div>
          <div class="form-group form-group-sm" title="Report Layout" v-if="enableMultipleLayout">
            <label class="sr-only">Layout</label>
            <select class="form-control input-sm" v-model="layout">
              <option value="compact">Compact</option>
              <option value="horizontal">Horizontal</option>
              <option value="vertical">Vertical</option>
            </select>
          </div>
          <div class="form-group">
            <div class="form-inline__divider"></div>
          </div>
          <div class="form-group form-group-sm">
            <div class="btn-toolbar">
              <div class="btn-group">
                <button type="button" class="btn btn-default btn-sm" :disabled="fetchingData" @click="update">Update</button>
              </div>
              <div class="btn-group">
                <button type="button" class="btn btn-default btn-sm" :disabled="fetchingData || isFormClear" @click="clear">Clear</button>
              </div>
            </div>
          </div>
          <div class="form-group">
            <div class="form-inline__divider"></div>
          </div>
          <div class="form-group">
            <div class="btn-toolbar">
              <div class="btn-group">
                <button type="button" class="btn btn-default btn-sm" @click="reload" title="Refresh Page" :disabled="fetchingData"><i class="fa fa-fw fa-refresh"></i></button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div><!-- .page-header -->
    <div class="page-body">
      <!-- COMPACT LAYOUT -->
      <table class="table table-bordered table-condensed table-hover report-table report-table--compact" v-if="isLayoutCompact">
        <thead>
          <tr class="report-table__header-1">
            <th rowspan="2" class="text-center part-number">Part #</th>
            <th rowspan="2" class="name">Name</th>
            <th rowspan="2" class="text-center category-status" title="Category Status">CS</th>
            <th rowspan="2" class="text-center product-status" title="Product Status">ST</th>
            <th rowspan="2" class="text-center current-cost">Sell $</th>
            <th rowspan="2" class="text-center current-price">Last Cost</th>
            <template v-for="(vendor, vkey) in vendorOptions">
              <th :key="vkey" rowspan="2" class="text-center current-price">{{ vendor }}<br/>Cost</th>
            </template>
            <th colspan="2" class="text-center">
              <select class="form-control input-sm" v-model="profitMarginTarget" title="Vendor from which to calculate profit margin">
                <option v-for="(target, key) in vendorProfitCalculationTargets" :key="key">{{ target }}</option>
              </select>
            </th>
          </tr>
          <tr class="report-table__header-2">
            <th class="text-center margin-amount" @click="toggleSort('profitAmount')">
              <i class="fa fa-fw fa-sort" v-show="!isSortSelected('profitAmount')"></i>
              <i class="fa fa-fw fa-sort-up" v-show="isSortAsc('profitAmount')"></i>
              <i class="fa fa-fw fa-sort-down" v-show="isSortDesc('profitAmount')"></i>
              Profit $
            </th>
            <th class="text-center margin-percentage" @click="toggleSort('profitPercentage')">
              <i class="fa fa-fw fa-sort" v-show="!isSortSelected('profitPercentage')"></i>
              <i class="fa fa-fw fa-sort-up" v-show="isSortAsc('profitPercentage')"></i>
              <i class="fa fa-fw fa-sort-down" v-show="isSortDesc('profitPercentage')"></i>
              Profit %
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-show="!hasItem">
            <td colspan="10" class="text-center">No product found!</td>
          </tr>
          <template v-for="(item, key) in items">
            <template v-if="item.hasAttributes">
              <tr :key="`item-${key}`" :class="{active: isRowActive(`item-${key}`)}" @click="setRowAsActive(`item-${key}`)">
                <td></td>
                <td class="name" title="Product Name">{{ item.details.name }}</td>
                <td></td>
                <td></td>
                <template v-for="(vendor, xkey) in vendorOptions">
                  <td :key="xkey"></td>
                </template>
                <td></td>
                <td></td>
              </tr>
              <template v-for="(part, pkey) in item.parts">
                <tr :key="`part-with-multi-attr-${pkey}`" :class="{active: isRowActive(`part-with-multi-attr-${pkey}`)}" @click="setRowAsActive(`part-with-multi-attr-${pkey}`)">
                  <td class="part-number text-center" title="Part Number">
                    <a target="_blank" :href="productLink(item, part)">{{ part.details.partNumber | normalizePartNumber }}</a>
                  </td>
                  <td class="attribute-name" title="Attribute Name">{{ part.details.optionValue }}</td>
                  <td class="text-center category-status" title="Category Status"><toggle :value="part.details.categoryEnabled"></toggle></td>
                  <td class="text-center product-status" title="Product Status">{{ availabilityToLabel(part.details.availability.stockStatus) }} <span v-if="part.details.qtyAvailable > 0" class="text-success">({{ part.details.qtyAvailable }})</span></td>
                  <td class="text-right current-cost" :class="{'text-danger': itemSellingPrice(item, part) <= 0}" title="Selling Price">
                    <money v-if="itemSellingPrice(item, part)" :value="moneyToLegacyType(itemSellingPrice(item, part))"></money>
                  </td>
                  <td class="text-right current-price" :class="{'text-danger': !part.details.lastCost}" title="Last Cost">
                    <a :href="vendorLink(item, part)" target="_blank">{{ vendorInitial(part.details.lastVendor) }}</a>&nbsp;
                    <money :value="moneyToLegacyType(part.details.lastCost)"></money>
                    <br/> {{ lastDateFormat(part.details.lastDate) }}
                  </td>
                  <template v-for="(vendor, vkey) in vendorOptions">
                    <td
                      :key="`${key}-${pkey}-${vkey}-1`"
                      class="text-center current-price"
                      title="Vendor Current Price"
                      >
                      <a
                        v-popover="{trigger: 'hover click', placement: 'bottom auto'}"
                        data-toggle="popover"
                        data-html="true"
                        data-title="Vendor Price Info"
                        :data-popover-template-id="`vendor-info-${key}-${pkey}-${vkey}`"
                        v-cloak
                        @click.stop.prevent
                        >
                        <money v-if="itemVendorCurrentPrice(item, part, vendor)" :value="moneyToLegacyType(itemVendorCurrentPrice(item, part, vendor))" title="Click to toggle holding info message"></money>
                        &nbsp;/&nbsp;{{ itemVendorQuantityOnhand(item, part, vendor) }}
                      </a>
                      <div v-show="false" :ref="`vendor-info-${key}-${pkey}-${vkey}`" :id="`vendor-info-${key}-${pkey}-${vkey}`">
                        <table class="table table-bordered table-condensed table-hover table--compact">
                          <thead>
                            <th class="text-center">Vendor</th>
                            <th class="text-center">Item #</th>
                            <th class="text-center">Prc</th>
                            <th class="text-center">Qty</th>
                            <th class="text-center">Last Update</th>
                          </thead>
                          <tbody>
                            <tr>
                              <td>{{ vendor }}</td>
                              <td class="text-center">{{ itemVendorNumber(item, part, vendor) }}</td>
                              <td class="text-center"><money :value="moneyToLegacyType(itemVendorCurrentPrice(item, part, vendor))"></money></td>
                              <td class="text-center">{{ itemVendorQuantityOnhand(item, part, vendor) }}</td>
                              <td class="text-center">{{ itemVendorLastUpdate(item, part, vendor) | dateFormat('MM/DD/YYYY') }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </template>
                  <td class="text-right margin-amount" :class="{'bg-danger': grossMarginProfitInAmountByFilter(item, part) < 0}" title="Gross Margin Profit $">
                    <money v-if="grossMarginProfitInAmountByFilter(item, part)" :value="moneyToLegacyType(grossMarginProfitInAmountByFilter(item, part))"></money>
                  </td>
                  <td class="text-right margin-percentage" :class="{'bg-danger': grossMarginProfitInPercentageByFilter(item, part) < 0}" title="Gross Margin Profit % 4">
                    {{ grossMarginProfitInPercentageByFilter(item, part) }}
                  </td>
                </tr>
              </template>
            </template>
            <template v-else-if="item.hasAttribute">
              <template v-for="(part, pkey) in item.parts">
                <tr :key="`part-with-attr-${pkey}`" :class="{active: isRowActive(`part-with-attr-${pkey}`)}" @click="setRowAsActive(`part-with-attr-${pkey}`)">
                  <td class="part-number text-center" title="Part Number">
                    <a target="_blank" :href="productLink(item, part)">{{ part.details.partNumber | normalizePartNumber }}</a>
                  </td>
                  <td class="attribute-name" title="Attribute Name" v-html="`${part.details.name }: ${part.details.optionValue}`"></td>
                  <td class="text-center category-status" title="Category Status"><toggle :value="part.details.categoryEnabled"></toggle></td>
                  <td class="text-center product-status" title="Product Status">{{ availabilityToLabel(part.details.availability.stockStatus) }} <span v-if="part.details.qtyAvailable > 0" class="text-success">({{ part.details.qtyAvailable }})</span></td>
                  <td class="text-right current-cost" :class="{'text-danger': itemSellingPrice(item, part) <= 0}" title="Selling Price">
                    <money v-if="itemSellingPrice(item, part)" :value="moneyToLegacyType(itemSellingPrice(item, part))"></money>
                  </td>
                  <td class="text-right current-price" :class="{'text-danger': !part.details.lastCost}" title="Last Cost">
                    <a :href="vendorLink(item, part)" target="_blank">{{ vendorInitial(part.details.lastVendor) }}</a>&nbsp;
                    <money :value="moneyToLegacyType(part.details.lastCost)"></money>
                    <br/> {{ lastDateFormat(part.details.lastDate) }}
                  </td>
                  <template v-for="(vendor, vkey) in vendorOptions">
                    <td
                      :key="`${key}-${pkey}-${vkey}-1`"
                      class="text-center current-price"
                      title="Vendor Current Price"
                      >
                      <a
                        v-popover="{trigger: 'hover click', placement: 'bottom auto'}"
                        data-toggle="popover"
                        data-html="true"
                        data-title="Vendor Price Info"
                        :data-popover-template-id="`vendor-info-${key}-${pkey}-${vkey}`"
                        v-cloak
                        @click.stop.prevent
                        >
                        <money v-if="itemVendorCurrentPrice(item, part, vendor)" :value="moneyToLegacyType(itemVendorCurrentPrice(item, part, vendor))" title="Click to toggle holding info message"></money>
                        &nbsp;/&nbsp;{{ itemVendorQuantityOnhand(item, part, vendor) }}
                      </a>
                      <div v-show="false" :ref="`vendor-info-${key}-${pkey}-${vkey}`" :id="`vendor-info-${key}-${pkey}-${vkey}`">
                        <table class="table table-bordered table-condensed table-hover table--compact">
                          <thead>
                            <th class="text-center">Vendor</th>
                            <th class="text-center">Item #</th>
                            <th class="text-center">Prc</th>
                            <th class="text-center">Qty</th>
                            <th class="text-center">Last Update</th>
                          </thead>
                          <tbody>
                            <tr>
                              <td>{{ vendor }}</td>
                              <td class="text-center">{{ itemVendorNumber(item, part, vendor) }}</td>
                              <td class="text-center"><money :value="moneyToLegacyType(itemVendorCurrentPrice(item, part, vendor))"></money></td>
                              <td class="text-center">{{ itemVendorQuantityOnhand(item, part, vendor) }}</td>
                              <td class="text-center">{{ itemVendorLastUpdate(item, part, vendor) | dateFormat('MM/DD/YYYY') }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </template>
                  <td class="text-right margin-amount" :class="{'bg-danger': grossMarginProfitInAmountByFilter(item, part) < 0}" title="Gross Margin Profit $">
                    <money v-if="grossMarginProfitInAmountByFilter(item, part)" :value="moneyToLegacyType(grossMarginProfitInAmountByFilter(item, part))"></money>
                  </td>
                  <td class="text-right margin-percentage" :class="{'bg-danger': grossMarginProfitInPercentageByFilter(item, part) < 0}" title="Gross Margin Profit % 3">
                    {{ grossMarginProfitInPercentageByFilter(item, part) }}
                  </td>
                </tr>
              </template>
            </template>
            <template v-else>
              <template v-for="(part, pkey) in item.parts">
                <tr :key="`part-wout-attr-${pkey}-${part.details.partNumber}`" :class="{active: isRowActive(`part-wout-attr-${pkey}`)}" @click="setRowAsActive(`part-wout-attr-${pkey}`)">
                  <td class="part-number text-center" title="Part Number">
                    <a target="_blank" :href="productLink(item, part)">{{ part.details.partNumber | normalizePartNumber }}</a>
                  </td>
                  <td class="name" title="Product Name">{{ part.details.name }}</td>
                  <td class="text-center category-status" title="Category Status"><toggle :value="part.details.categoryEnabled"></toggle></td>
                  <td class="text-center product-status" title="Product Status">{{ availabilityToLabel(part.details.availability.stockStatus) }} <span v-if="part.details.qtyAvailable > 0" class="text-success">({{ part.details.qtyAvailable }})</span></td>
                  <td class="text-right current-cost" :class="{'text-danger': itemSellingPrice(item, part) <= 0}" title="Selling Price">
                    <money v-if="itemSellingPrice(item, part)" :value="moneyToLegacyType(itemSellingPrice(item, part))"></money>
                    <a title="Edit Price" @click.prevent="editPrice(item, part)"><i class="fa fa-fw fa-pencil"></i></a>
                  </td>
                  <td class="text-right current-price" :class="{'text-danger': !part.details.lastCost}" title="Last Cost">
                    <template v-if="part.details.lastCost">
                      <a :href="vendorLink(item, part)" target="_blank">{{ vendorInitial(part.details.lastVendor) }}</a>&nbsp;
                      <money :value="moneyToLegacyType(part.details.lastCost)"></money>
                      <br/> {{ lastDateFormat(part.details.lastDate) }}
                    </template>
                  </td>
                  <template v-for="(vendor, vkey) in vendorOptions">
                    <td
                      :key="`${key}-${pkey}-${vkey}-1`"
                      class="text-center current-price"
                      title="Vendor Current Price"
                      >
                        <a
                          v-popover="{trigger: 'hover click', placement: 'bottom auto'}"
                          data-toggle="popover"
                          data-html="true"
                          data-title="Vendor Price Info"
                          :data-popover-template-id="`vendor-info-${key}-${pkey}-${vkey}`"
                          v-cloak
                          @click.stop.prevent
                          >
                          <money v-if="itemVendorCurrentPrice(item, part, vendor)" :value="moneyToLegacyType(itemVendorCurrentPrice(item, part, vendor))" title="Click to toggle holding info message"></money>
                          <span v-if="itemVendorQuantityOnhand(item, part, vendor)" title="Click to toggle holding info message">&nbsp;/&nbsp;{{ itemVendorQuantityOnhand(item, part, vendor) }}</span>
                        </a>
                        <div v-show="false" :ref="`vendor-info-${key}-${pkey}-${vkey}`" :id="`vendor-info-${key}-${pkey}-${vkey}`">
                          <table class="table table-bordered table-condensed table-hover table--compact">
                            <thead>
                              <th class="text-center">Vendor</th>
                              <th class="text-center">Item #</th>
                              <th class="text-center">Prc</th>
                              <th class="text-center">Qty</th>
                              <th class="text-center">Last Update</th>
                            </thead>
                            <tbody>
                              <tr>
                                <td>{{ vendor }}</td>
                                <td class="text-center">{{ itemVendorNumber(item, part, vendor) }}</td>
                                <td class="text-center"><money :value="moneyToLegacyType(itemVendorCurrentPrice(item, part, vendor))"></money></td>
                                <td class="text-center">{{ itemVendorQuantityOnhand(item, part, vendor) }}</td>
                                <td class="text-center">{{ itemVendorLastUpdate(item, part, vendor) | dateFormat('MM/DD/YYYY') }}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                    </td>
                  </template>
                  <td class="text-right margin-amount" :class="{'bg-danger': grossMarginProfitInAmountByFilter(item, part) < 0}" title="Gross Margin Profit $">
                    <money v-if="grossMarginProfitInAmountByFilter(item, part)" :value="moneyToLegacyType(grossMarginProfitInAmountByFilter(item, part))"></money>
                  </td>
                  <td class="text-right margin-percentage" :class="{'bg-danger': grossMarginProfitInPercentageByFilter(item, part) < 0}" title="Gross Margin Profit % 2">
                    {{ grossMarginProfitInPercentageByFilter(item, part) }}
                  </td>
                </tr>
              </template>
            </template>
          </template>
        </tbody>
        <tfoot v-if="isPaginationApplicable">
          <tr>
            <td colspan="12">
              <pagination
                class="text-center pagination-compact"
                :p-total="pagination.total"
                :p-per-page="pagination.perPage"
                :p-page="pagination.page"
                @open-page="openPage"
                ></pagination>
            </td>
          </tr>
        </tfoot>
      </table>
      <!-- HORIZONTAL LAYOUT -->
      <table class="table table-bordered table-condensed table-hover report-table report-table--horizontal" v-if="isLayoutHorizontal">
        <thead>
          <tr class="report-table__header-2">
            <th colspan="3"></th>
            <th colspan="3" class="text-center">Tactical</th>
            <th colspan="3" class="text-center" v-for="(vendor, key) in vendors" :key="key">{{ vendor }}</th>
          </tr>
          <tr class="report-table__header-2">
            <th colspan="2" class="name">Name</th>
            <th class="text-right">Cost</th>
            <th class="text-right bg-info">Price</th>
            <th class="text-right">Profit $</th>
            <th class="text-right">Profit %</th>
            <template v-for="(vendor, key) in vendors">
              <th :key="`${key}-2`" class="text-right bg-info">Price</th>
              <th :key="`${key}-3`" class="text-right">Profit $</th>
              <th :key="`${key}-4`" class="text-right">Profit %</th>
            </template>
          </tr>
        </thead>
        <tbody>
          <template v-for="(item, key) in items">
            <tr :key="key">
              <td colspan="2" class="name bg-warning" title="Product Name">{{ item.details.name }}</td>
              <td></td>
              <td class="bg-info"></td>
              <td></td>
              <td></td>
              <template v-for="(v, k) in vendors">
                <td :key="`${k}-1`" class="bg-info"></td>
                <td :key="`${k}-2`"></td>
                <td :key="`${k}-3`"></td>
              </template>
            </tr>
            <template v-for="(part, pkey) in item.parts">
              <tr :key="pkey">
                <td class="part-number text-center" title="Part Number">
                  <a target="_blank" :href="productLink(item, part)">{{ part.details.partNumber }}</a>
                </td>
                <td class="attribute-name" title="Attribute Name">{{ part.details.optionValue }}</td>
                <td class="text-right current-cost" title="Current Cost"><money :value="moneyToLegacyType(part.details.lastCost)"></money></td>
                <td class="text-right current-price bg-info" title="Current Price"><money :value="moneyToLegacyType(itemSellingPrice(item, part))"></money></td>
                <td class="text-right margin-amount" title="Gross Margin Profit $"><money :value="moneyToLegacyType(grossMarginProfitInAmount(part.details.currentPrice, part.details.lastCost))"></money></td>
                <td class="text-right margin-percentage" title="Gross Margin Profit %">{{ grossMarginProfitInPercentage(part.details.currentPrice, part.details.lastCost) }}</td>
                <template v-for="(vendor, vkey) in part.vendors">
                  <td :key="`${key}-${pkey}-${vkey}-1`" class="text-right current-price bg-info" title="Vendor Current Price"><money :value="moneyToLegacyType(vendor.vendorCurrentPrice)"></money></td>
                  <td :key="`${key}-${pkey}-${vkey}-2`" class="text-right margin-amount" title="Gross Margin Profit $"><money :value="moneyToLegacyType(grossMarginProfitInAmount(part.details.currentPrice, vendor.vendorCurrentPrice))"></money></td>
                  <td :key="`${key}-${pkey}-${vkey}-3`" class="text-right margin-percentage" title="Gross Margin Profit %">{{ grossMarginProfitInPercentage(part.details.currentPrice, vendor.vendorCurrentPrice) }}</td>
                </template>
              </tr>
            </template>
          </template>
        </tbody>
        <tfoot v-if="isPaginationApplicable">
          <tr>
            <td colspan="12">
              <pagination
                class="text-center pagination-compact"
                :p-total="pagination.total"
                :p-per-page="pagination.perPage"
                :p-page="pagination.page"
                @open-page="openPage"
                ></pagination>
            </td>
          </tr>
        </tfoot>
      </table>
      <!-- VERTICAL LAYOUT -->
      <table class="table table-bordered table-condensed table-hover report-table report-table--vertical" v-if="isLayoutVertical">
        <thead>
          <tr class="report-table__header-2">
            <th colspan="2" class="name">Name</th>
            <th class="text-right current-price">Last Cost</th>
            <th class="text-right current-price">Current $</th>
            <th class="text-right margin-amount" title="Gross Profit Margin Amount">GPM $</th>
            <th class="text-right margin-percentage" title="Gross Profit Margin Percentage">GPM %</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="(item, key) in items">
            <tr :key="key" class="bg-warning">
              <td colspan="3" class="product-name" title="Product Name">{{ item.details.name }}</td>
              <td colspan="3"></td>
            </tr>
            <template  v-for="(part, pkey) in item.parts">
              <tr :key="`${key}-${pkey}`">
                <td class="part-number" title="Part Number">
                  <a target="_blank" :href="productLink(item, part)">{{ part.details.partNumber }}</a>
                </td>
                <td class="attribute-name" title="Attribute Name">{{ part.details.optionValue }}</td>
                <td class="text-right amount last-cost" title="Last Cost"><money :value="moneyToLegacyType(part.details.lastCost)"></money></td>
                <td class="text-right amount current-price bg-info" title="Current Price"><money :value="moneyToLegacyType(itemSellingPrice(item, part))"></money></td>
                <td class="text-right amount margin-amount" title="Gross Margin Profit $"><money :value="moneyToLegacyType(grossMarginProfitInAmount(part.details.currentPrice, part.details.lastCost))"></money></td>
                <td class="text-right amount margin-percentage" title="Gross Margin Profit %">{{ grossMarginProfitInPercentage(part.details.currentPrice, part.details.lastCost) }}</td>
              </tr>
              <tr v-for="(vendor, vkey) in part.vendors" :key="`${key}-${pkey}-${vkey}`">
                <td class="vendor-part-number" title="Vendor Part Number">{{ vendor.vendorPartNumber }}</td>
                <td colspan="2" class="vendor-company" title="Vendor Company">{{ vendor.vendorCompany }}</td>
                <td class="text-right amount current-price bg-info" title="Current Price"><money :value="moneyToLegacyType(vendor.vendorCurrentPrice)"></money></td>
                <td class="text-right amount margin-amount" title="Gross Margin Profit $"><money :value="moneyToLegacyType(grossMarginProfitInAmount(part.details.currentPrice, vendor.vendorCurrentPrice))"></money></td>
                <td class="text-right amount margin-percentage" title="Gross Margin Profit %">{{ grossMarginProfitInPercentage(part.details.currentPrice, vendor.vendorCurrentPrice) }}</td>
              </tr>
            </template>
          </template>
        </tbody>
        <tfoot v-if="isPaginationApplicable">
          <tr>
            <td colspan="12">
              <pagination
                class="text-center pagination-compact"
                :p-total="pagination.total"
                :p-per-page="pagination.perPage"
                :p-page="pagination.page"
                @open-page="openPage"
                ></pagination>
            </td>
          </tr>
        </tfoot>
      </table>
    </div><!-- .page-body -->
    <modal
      effect="zoom"
      ok-text="Submit"
      size="large"
      :callback="savePrice"
      :show="editPriceModal.visible"
      @close="hideEditPriceForm"
      >
      <div slot="title">
        Edit Price
        <small v-if="editPriceModal.part">{{editPriceModal.part.details.partNumber | normalizePartNumber}} {{editPriceModal.part.details.name}}</small>
      </div>
      <div slot="content" class="clearfix">
        <form v-on:submit.prevent="savePrice">
          <button class="hidden">Required For Listening To Form Submission</button>
          <div class="form-horizontal">
            <div class="form-group form-group-sm">
              <label class="col-lg-3 control-label">Retail Price</label>
              <div class="col-lg-3">
                <input class="form-control text-right" type="text" v-model="editPriceModal.form.retailPrice" />
              </div>
            </div>
            <div class="form-group form-group-sm">
              <label class="col-lg-3 control-label">Sale Price</label>
              <div class="col-lg-3">
                <div class="input-group input-group-sm">
                  <span class="input-group-addon">
                    <toggle v-model="editPriceModal.form.salePriceStatus" class="toggle--input-group-addon" title="Toggle Sale Price Status" :disabled="isSalePriceEmpty"></toggle>
                  </span>
                  <input class="form-control text-right" type="text" v-model="editPriceModal.form.salePrice" :disabled="editPriceModal.form.cashDiscountStatus" />
                </div><!-- /input-group -->
              </div>
            </div>
            <div class="form-group form-group-sm">
              <label class="col-lg-3 control-label">Cash Discount Price</label>
              <div class="col-lg-3">
                <div class="input-group input-group-sm">
                  <span class="input-group-addon">
                    <toggle v-model="editPriceModal.form.cashDiscountStatus" class="toggle--input-group-addon" title="Toggle Cash Discount Price Status"></toggle>
                  </span>
                  <input class="form-control text-right" type="text" v-model="editPriceModal.form.cashDiscount" :disabled="!editPriceModal.form.cashDiscountStatus" />
                </div><!-- /input-group -->
              </div>
            </div>
          </div>
        </form>
      </div>
    </modal>
  </div><!-- #tac-admin-sales-report-low-price-alert__list -->
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import { mapState, mapGetters } from 'vuex'
import { format as DateFormat } from '@/plugin/filter/date'
import ApiResponseMixin from '@/plugin/mixin/api-response'
import Modal from '@/plugin/bootstrap/modal'
import Money from '@/plugin/element/money'
import MoneyMixin from '@/plugin/mixin/money'
import NormalizePartNumber from '@/plugin/filter/normalize-part-number';
import Pagination from '@/plugin/bootstrap/pagination';
import Popover from '@/plugin/directive/popover'
import Toggle from '@/plugin/bootstrap/toggle';
import Tooltip from '@/plugin/directive/tooltip'

const VENDOR_COMPANY_TAC = 'Tactical Innovations Inc.'
const VENDOR_COMPANY_RSR = 'RSR Group Inc.'
const VENDOR_COMPANY_SS  = 'Sports South Inc'

const VENDOR_INITIAL_TAC = 'TAC'
const VENDOR_INITIAL_RSR = 'RSR'
const VENDOR_INITIAL_SS  = 'SS'

export default {
  data() {
    return {
      layout: 'compact',
      profitMarginTarget: VENDOR_INITIAL_TAC,
      productLinkToUse: 'old',
      filter: {
        vendor: 'all',
        productId: null,
        categoryId: null,
      },
      sorter: {
        profitAmount: null,
        profitPercentage: null,
      },
      activeRow: null,
      enableMultipleLayout: false,
      editPriceModal: {
        visible: false,
        item: null,
        part: null,
        form: {
          productId: null,
          retailPrice: null,
          salePrice: null,
          salePriceStatus: null,
          cashDiscount: null,
          cashDiscountStatus: null
        }
      }
    }
  },
  created() {
    this.loadFilterFromRoute()
    this.loadSorterFromRoute()
    this.loadPaginationFromRoute()
    this.fetchReport()
    this.fetchOptions()
  },
  watch: {
    'sorter': {
      deep: true,
      handler() {
        this.update()
      }
    },
    'editPriceModal.form.salePrice'(val) {
      const prc = parseFloat(val)
      if (prc > 0) {
        return
      }

      if (!this.editPriceModal.form.salePriceStatus) {
        return
      }

      this.editPriceModal.form.salePriceStatus = false
    },
    'editPriceModal.form.cashDiscount'(val) {
      if (!this.editPriceModal.form.cashDiscountStatus) {
        return
      }

      const prc = parseFloat(val)
      this.editPriceModal.form.salePrice = prc + (prc * 0.03)
    },
    'editPriceModal.form.cashDiscountStatus'(val) {
      if (!val) {
        return
      }

      const prc = this.editPriceModal.form.cashDiscount
      if (prc) {
        this.editPriceModal.form.salePrice = prc + (prc * 0.03)
      } else {
        this.editPriceModal.form.salePrice = 0
      }
    }
  },
  computed: {
    ...mapState('sales/report/lowPriceAlert', {
      items: 'aggregate',
      pagination: 'pagination'
    }),
    ...mapGetters('sales/report/lowPriceAlert', [
      'vendors',
      'vendorOptions',
      'vendorProfitCalculationTargets',
      'isRequestingServer',
      'isPaginationApplicable',
      'categoryOptions'
    ]),
    isLayoutCompact() {
      return 'compact' === this.layout
    },
    isLayoutHorizontal() {
      return 'horizontal' === this.layout
    },
    isLayoutVertical() {
      return 'vertical' === this.layout
    },
    isOldProductLinkToUse() {
      return 'old' === this.productLinkToUse
    },
    isNewProductLinkToUse() {
      return 'new' === this.productLinkToUse
    },
    isSalePriceEmpty() {
      return !parseFloat(this.editPriceModal.form.salePrice)
    },
    fetchingData() {
      return this.isRequestingServer
    },
    isFormClear() {
      return 'all' === this.filter.vendor
        && _.includes([undefined, null, '', 0], this.filter.productId)
        && _.includes([undefined, null, '', 0], this.filter.categoryId)
    },
    hasItem() {
      return !!_.size(this.items)
    }
  },
  methods: {
    loadFilterFromRoute() {
      const productId = this.$route.query.productId
      if (undefined !== productId) {
        this.filter.productId = productId
        this.$store.dispatch('sales/report/lowPriceAlert/filterByProductId', productId)
      }
      const fromVendor = this.$route.query.fromVendor
      if (undefined !== fromVendor) {
        this.filter.vendor = fromVendor
        this.$store.dispatch('sales/report/lowPriceAlert/showProductsFromVendor', fromVendor)
      }
      const categoryId = this.$route.query.categoryId
      if (undefined !== categoryId) {
        this.filter.categoryId = categoryId
        this.$store.dispatch('sales/report/lowPriceAlert/filterByCategoryId', categoryId)
      }
    },
    loadSorterFromRoute() {
      const sortProfitAmount = this.$route.query.sortProfitAmount
      if (undefined !== sortProfitAmount) {
        this.sorter.sortProfitAmount = sortProfitAmount
        this.$store.dispatch('sales/report/lowPriceAlert/sortByProfitAmount', sortProfitAmount)
      }
      const sortProfitPercentage = this.$route.query.sortProfitPercentage
      if (undefined !== sortProfitPercentage) {
        this.sorter.vendor = sortProfitPercentage
        this.$store.dispatch('sales/report/lowPriceAlert/sortByProfitPercentage', sortProfitPercentage)
      }
    },
    loadPaginationFromRoute() {
      const page = parseInt(this.$route.query.page)
      if (page && 1 !== page) {
        this.$store.dispatch('sales/report/lowPriceAlert/changePage', page)
      }
      const perPage = parseInt(this.$route.query.perPage)
      if (perPage && 10 !== perPage) {
        this.$store.dispatch('sales/report/lowPriceAlert/changeItemsPerPage', perPage)
      }
    },
    openPage(page) {
      this.$store.dispatch('sales/report/lowPriceAlert/changePage', page)

      let query = _.assign({}, this.$route.query, {page: page})
      if (query.page && 1 == query.page) {
        delete query.page
      }
      if (query.itemsPerPage && 10 == query.itemsPerPage) {
        delete query.itemsPerPage
      }

      this.$router.push({
        name   : this.$route.name,
        params : _.assign({}, this.$route.params),
        query  : query,
      })

      this.fetchReport()
    },
    calculateProfitMargin(forVendor) {
      this.$store.dispatch('sales/report/lowPriceAlert/calculateProfitMargin', forVendor)

      let query = _.assign({}, this.$route.query, {profitMarginTarget: forVendor})
      if (query.profitMarginTarget && 'all' == query.profitMarginTarget) {
        delete query.profitMarginTarget
      }

      this.$router.push({
        name   : this.$route.name,
        params : _.assign({}, this.$route.params),
        query  : query,
      })

      this.fetchReport()
    },
    showProductsFromVendor(fromVendor) {
      this.$store.dispatch('sales/report/lowPriceAlert/showProductsFromVendor', fromVendor)

      let query = _.assign({}, this.$route.query, {fromVendor: fromVendor})
      if (query.fromVendor && 'all' == query.fromVendor) {
        delete query.fromVendor
      }

      this.$router.push({
        name   : this.$route.name,
        params : _.assign({}, this.$route.params),
        query  : query,
      })

      this.fetchReport()
    },
    filterByProductId(productId) {
      this.$store.dispatch('sales/report/lowPriceAlert/filterByProductId', productId)

      let query = _.assign({}, this.$route.query, {productId: productId})
      if (query.productId && 'all' == query.productId) {
        delete query.productId
      }

      this.$router.push({
        name   : this.$route.name,
        params : _.assign({}, this.$route.params),
        query  : query,
      })

      this.fetchReport()
    },
    reload() {
      this.fetchReport()
      this.fetchOptions()
    },
    clear() {
      this.filter.vendor = 'all'
      this.filter.productId = null
      this.filter.categoryId = null
      this.update()
    },
    update() {
      this.$store.dispatch('sales/report/lowPriceAlert/showProductsFromVendor', this.filter.vendor)
      this.$store.dispatch('sales/report/lowPriceAlert/filterByProductId', this.filter.productId)
      this.$store.dispatch('sales/report/lowPriceAlert/filterByCategoryId', this.filter.categoryId)
      this.$store.dispatch('sales/report/lowPriceAlert/sortByProfitAmount', this.sorter.profitAmount)
      this.$store.dispatch('sales/report/lowPriceAlert/sortByProfitPercentage', this.sorter.profitPercentage)
      this.$store.dispatch('sales/report/lowPriceAlert/changePage', 1)

      let query = _.assign({}, this.$route.query, {
        fromVendor : this.filter.vendor,
        productId  : this.filter.productId,
        categoryId : this.filter.categoryId,
        'so-pa'    : this.sorter.profitAmount,
        'so-pp'    : this.sorter.profitPercentage,
      })
      if (query.fromVendor && 'all' == query.fromVendor) {
        delete query.fromVendor
      }
      if (_.has(query, 'productId') && _.includes([null, '', 'all'], query.productId)) {
        delete query.productId
      }
      if (_.has(query, 'categoryId') && _.includes([null, ''], query.categoryId)) {
        delete query.categoryId
      }
      if (_.has(query, 'so-pa') && _.includes([null, ''], query['so-pa'])) {
        delete query['so-pa']
      }
      if (_.has(query, 'so-pp') && _.includes([null, ''], query['so-pp'])) {
        delete query['so-pp']
      }
      if (query.page) {
        delete query.page
      }

      this.$router.push({
        name   : this.$route.name,
        params : _.assign({}, this.$route.params),
        query  : query,
      })

      this.fetchReport()
    },
    fetchReport() {
      this.$store.dispatch('sales/report/lowPriceAlert/fetchReport')
    },
    fetchOptions() {
      this.$store.dispatch('sales/report/lowPriceAlert/fetchOptions')
    },
    isRowActive(row) {
      return this.activeRow === row
    },
    setRowAsActive(row) {
      this.activeRow = row
    },
    lastDateFormat(lastDate) {
      if (!lastDate) {
        return null
      }
      if ('0000-00-00 00:00:00' === lastDate) {
        return null
      }

      return moment(new Date(lastDate)).format('YYYYMMDD')
    },
    productLink(item, part) {
      if (this.isOldProductLinkToUse) {
        return `/admin/categories.php?pID=${part.details.productId}&action=new_product`
      }
      if (this.isNewProductLinkToUse) {
        return `/admin/product/view/${part.details.productId}`
      }
    },
    vendorLink(item, part) {
      return `/admin/manufacturers.php?mID=${part.details.vendorId}&action=view`
    },
    grossMarginProfitInAmountByFilter(item, part) {
      let partVendor = null
      if (VENDOR_INITIAL_SS === this.profitMarginTarget) {
        partVendor = _.find(part.vendors, {vendorCompany: VENDOR_COMPANY_SS})
      } else if (VENDOR_INITIAL_RSR === this.profitMarginTarget) {
        partVendor = _.find(part.vendors, {vendorCompany: VENDOR_COMPANY_RSR})
      }

      const sellingPrice = this.itemSellingPrice(item, part)

      let amount = null
      if (VENDOR_INITIAL_TAC === this.profitMarginTarget) {
        amount = this.grossMarginProfitInAmount(sellingPrice, part.details.lastCost)
      } else if (partVendor) {
        amount = this.grossMarginProfitInAmount(sellingPrice, partVendor.vendorCurrentPrice)
      }

      return amount
    },
    grossMarginProfitInPercentageByFilter(item, part) {
      let partVendor = null
      if (VENDOR_INITIAL_SS === this.profitMarginTarget) {
        partVendor = _.find(part.vendors, {vendorCompany: VENDOR_COMPANY_SS})
      } else if (VENDOR_INITIAL_RSR === this.profitMarginTarget) {
        partVendor = _.find(part.vendors, {vendorCompany: VENDOR_COMPANY_RSR})
      }

      const sellingPrice = this.itemSellingPrice(item, part)

      let amount = null
      if (VENDOR_INITIAL_TAC === this.profitMarginTarget) {
        amount = this.grossMarginProfitInPercentage(sellingPrice, part.details.lastCost)
      } else if (partVendor) {
        amount = this.grossMarginProfitInPercentage(sellingPrice, partVendor.vendorCurrentPrice)
      }

      return amount
    },
    itemSellingPrice(item, part) {
      if (part.details.salePriceStatus && part.details.salePrice) {
        return part.details.salePrice
      }
      return part.details.currentPrice
    },
    itemVendorCurrentPrice(item, part, initial) {
      const company = this.vendorCompany(initial)

      const partVendor = _.find(part.vendors, (partVendor) => {
        return partVendor.vendorCompany === company
      })

      if (!partVendor) {
        return 0
      }

      return partVendor.vendorCurrentPrice || 0
    },
    itemVendorLastUpdate(item, part, initial) {
      const company = this.vendorCompany(initial)

      const partVendor = _.find(part.vendors, (partVendor) => {
        return partVendor.vendorCompany === company
      })

      if (!partVendor) {
        return null
      }

      return partVendor.entryDate
    },
    itemVendorQuantityOnhand(item, part, initial) {
      const company = this.vendorCompany(initial)

      const partVendor = _.find(part.vendors, (partVendor) => {
        return partVendor.vendorCompany === company
      })

      if (!partVendor) {
        return 0
      }

      return partVendor.vendorQuantityOnhand
    },
    itemVendorNumber(item, part, initial) {
      const company = this.vendorCompany(initial)

      const partVendor = _.find(part.vendors, (partVendor) => {
        return partVendor.vendorCompany === company
      })

      if (!partVendor) {
        return 0
      }

      return partVendor.vendorPartNumber
    },
    vendorInitial(name) {
      if (VENDOR_COMPANY_TAC === name) {
        return VENDOR_INITIAL_TAC
      }
      if (VENDOR_COMPANY_RSR === name) {
        return VENDOR_INITIAL_RSR
      }
      if (VENDOR_COMPANY_SS === name) {
        return VENDOR_INITIAL_SS
      }

      return name
    },
    vendorCompany(initial) {
      if (VENDOR_INITIAL_TAC === initial) {
        return VENDOR_COMPANY_TAC
      }
      if (VENDOR_INITIAL_RSR === initial) {
        return VENDOR_COMPANY_RSR
      }
      if (VENDOR_INITIAL_SS === initial) {
        return VENDOR_COMPANY_SS
      }

      return name
    },
    grossMarginProfitInAmount(SP, BP) {
      if (!SP) {
        return null
      }
      if (!BP) {
        return null
      }
      // profit formula = ((SP - BP) / BP) * 100%
      // where BP = BUYING PRICE; SP = SELLING PRICES
      // NOTE: BP is also equal to CALCULATED GROSS EACH
      return _.round(SP - BP, 2)
    },
    grossMarginProfitInPercentage(SP, BP) {
      if (!SP) {
        return null
      }
      if (!BP) {
        return null
      }
      // profit formula = ((SP - BP) / BP) * 100%
      // where BP = BUYING PRICE; SP = SELLING PRICES
      // NOTE: BP is also equal to CALCULATED GROSS EACH
      return _.round(((SP - BP) / BP) * 100, 2)
    },
    optSpacer(str, cnt) {
      return str.repeat(cnt)
    },
    availabilityToLabel(val) {
      switch (val) {
        case 'instock':
          return 'Instock'
        case 'backorder':
          return 'Backorder'
        case 'due':
          return 'Due'
        case 'off':
          return 'Off'
        case 'inactive':
          return 'Discontinued'
        case 'discontinued':
          return 'Reference Part Only'
        case 'comingsoon':
          return 'Coming Soon'
      }

      return val
    },
    toggleSort(name) {
      const currentSort = this.sorter[name]

      this.clearSorts()

      if ('asc' === currentSort) {
        this.sorter[name] = 'desc'
      } else {
        this.sorter[name] = 'asc'
      }
    },
    isSortSelected(name) {
      return this.isSortAsc(name) || this.isSortDesc(name)
    },
    isSortAsc(name) {
      return 'asc' === _.toLower(this.sorter[name] || '')
    },
    isSortDesc(name) {
      return 'desc' === _.toLower(this.sorter[name] || '')
    },
    clearSorts() {
      this.sorter.profitAmount     = null
      this.sorter.profitPercentage = null
    },
    editPrice(item, part) {
      this.editPriceModal.item = item
      this.editPriceModal.part = part

      this.editPriceModal.form.productId          = part.details.productId
      this.editPriceModal.form.retailPrice        = this.moneyToLegacyType(part.details.currentPrice)
      this.editPriceModal.form.salePrice          = this.moneyToLegacyType(part.details.salePrice)
      this.editPriceModal.form.salePriceStatus    = part.details.salePriceStatus
      this.editPriceModal.form.cashDiscount       = this.moneyToLegacyType(part.details.cashPrice)
      this.editPriceModal.form.cashDiscountStatus = part.details.cashPriceStatus

      this.showEditPriceForm()
    },
    async savePrice() {
      let form = _.assign({}, this.editPriceModal.form)

      form.retailPrice  = this.legacyToMoneyType(form.retailPrice)
      form.salePrice    = this.legacyToMoneyType(form.salePrice)
      form.cashDiscount = this.legacyToMoneyType(form.cashDiscount)

      try {
        await this.$store.dispatch('sales/report/lowPriceAlert/savePrice', form)
        this.$alertify.success('Price updated!')
      } catch (e) {
        if (this.isResponseError(e)) {
          this.raiseErrorMessage(e)
        } else {
          this.$alertify.error(`Failed to update price! Reason: ${e}`)
        }
        throw e
      }

      this.cancelEditPrice()
      this.fetchReport()
    },
    cancelEditPrice() {
      this.editPriceModal.item = null
      this.editPriceModal.part = null

      this.editPriceModal.form.productId          = null
      this.editPriceModal.form.retailPrice        = null
      this.editPriceModal.form.salePrice          = null
      this.editPriceModal.form.salePriceStatus    = null
      this.editPriceModal.form.cashDiscount       = null
      this.editPriceModal.form.cashDiscountStatus = null

      this.hideEditPriceForm()
    },
    showEditPriceForm() {
      this.editPriceModal.visible = true
    },
    hideEditPriceForm() {
      this.editPriceModal.visible = false
    }
  },
  components: {
    Modal,
    Money,
    Pagination,
    Toggle
  },
  directives: {
    popover: Popover,
    tooltip: Tooltip
  },
  filters: {
    normalizePartNumber: NormalizePartNumber,
    dateFormat: DateFormat,
  },
  mixins: [
    ApiResponseMixin,
    MoneyMixin
  ]
}
</script>

<style lang="scss" scoped>
  .vendor-part-number {
    text-indent: 20px;
  }
  .attribute-name {
    text-indent: 25px;
  }
  .vendor-company {
    text-indent: 50px;
  }
  .current-cost,
  .current-price,
  .margin-amount,
  .margin-percentage,
  .vendor-retail,
  .vendor-dealer {
    width: 110px;
  }
  .part-number {
    width: 100px;
  }
  .opt-level-1 {
    text-indent: 20px;
  }
  .opt-level-2 {
    text-indent: 40px;
  }
  .opt-level-3 {
    text-indent: 60px;
  }
  .opt-level-4 {
    text-indent: 80px;
  }
</style>